import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { AppSelect as Select } from '../../../../../../../components/Select'

import { getPayoutCashBoxLookup, payOverpaymentViaLocalAdapter } from '../../core/api'
import { CashBoxLookupItem } from '../../core/response.types'
import { LOCAL_ADAPTER_ERROR_LABEL, PAY_VIA_LOCAL_ADAPTER_LABEL } from '../../core/consts'

interface Props {
  show: boolean
  overpaymentId: number | null
  onClose: () => void
  onDone: () => void
}

interface Option {
  value: number
  label: string
}

/**
 * Выбор кассы, которая проведёт зависшую в мэтчинге переоплату через локальный адаптер.
 *
 * Список касс — все включённые и незаблокированные, одинаковый для всех ролей, которым действие
 * открыто; сужать его здесь нечем и не нужно.
 */
export const LocalAdapterModal: React.FC<Props> = ({ show, overpaymentId, onClose, onDone }) => {
  const [cashBoxes, setCashBoxes] = useState<CashBoxLookupItem[]>([])
  const [cashBoxId, setCashBoxId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!show) {
      return
    }

    setCashBoxId(null)
    setError(null)

    getPayoutCashBoxLookup()
      .then((r) => {
        setCashBoxes(r.data.success ? r.data.data ?? [] : [])
      })
      .catch(() => setCashBoxes([]))
  }, [show])

  const options = useMemo<Option[]>(
    () => cashBoxes.map((c) => ({ value: c.value, label: c.label })),
    [cashBoxes]
  )

  const selected = options.find((o) => o.value === cashBoxId) ?? null

  const submit = async () => {
    if (overpaymentId === null || cashBoxId === null) {
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const response = await payOverpaymentViaLocalAdapter(overpaymentId, { cashBoxId })

      if (response.data?.success) {
        onDone()
        onClose()
        return
      }

      setError(readError(response.data?.errors))
    } catch (e: unknown) {
      // Перехватчик axios реджектит сам response, поэтому ошибки лежат в error.data.
      setError(readError((e as { data?: { errors?: Record<string, string> } })?.data?.errors))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{PAY_VIA_LOCAL_ADAPTER_LABEL}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-3'>
          <label className='form-label required'>Касса</label>
          <Select<Option>
            options={options}
            value={selected}
            onChange={(o) => setCashBoxId(o ? o.value : null)}
            placeholder='Выберите кассу'
            isClearable
            noOptionsMessage={() => 'Доступных касс нет'}
          />
          <div className='form-text'>
            Выплата уйдёт на локальный адаптер и попадёт в очередь выбранной кассы. Переоплата
            закроется, когда кассир подтвердит выплату.
          </div>
        </div>
        {error && <div className='alert alert-danger py-2 mb-0'>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
          Отмена
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={submit}
          disabled={submitting || cashBoxId === null}
        >
          {submitting ? 'Отправка…' : 'Подтвердить'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

/**
 * Бэкенд отдаёт код отказа в `errors.cashBoxId`. Незнакомый код показываем как есть — по нему
 * причину можно найти в логах, а пустое место не говорит ничего.
 */
function readError(errors?: Record<string, string>): string {
  const code = errors?.cashBoxId ?? Object.values(errors ?? {})[0]

  if (!code) {
    return 'Не удалось провести переоплату через локальный адаптер'
  }

  return LOCAL_ADAPTER_ERROR_LABEL[code] ?? code
}

export default LocalAdapterModal
